import {
  AlignmentType,
  ISectionOptions,
  Paragraph,
  SectionType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from "docx";
import { useChecklistCellMargins } from "./useChecklistCellMargins";
import { useWordChecklistCriteriaGenerator } from "./useWordChecklistCriteriaGenerator";
import { useWordChecklistSectionGenerator } from "./useWordChecklistSectionGenerator";
import { useWordFontSize } from "./useWordFontSize";
import { useWordPageSize } from "./useWordPageSize";

import { Paragraph as RevisionParagraph } from "~/types/Paragraph";

import { Checklist, ChecklistCriteria } from "types/Checklist";
import { SubmittedDocument } from "types/SubmittedDocument";
import { CellTextParagraphs, WordChecklistCells } from "types/WordChecklist";
import { useCalculateNumParagraphColumns } from "./useCalculateNumParagraphColumns";

import { DocumentSubmissionType } from "~/types/enums/DocumentSubmissionType.enum";

import {
  DocumentGradedState,
  DocumentGradedStateLabels,
} from "~/types/enums/DocumentGradedState.enum";

export const useWordChecklistGenerator = (
  submittedDocument: SubmittedDocument,
  checklist: Checklist | undefined,
  wordChecklistCells: WordChecklistCells,
  allCriteria: ChecklistCriteria[],
  totalEarnedScore: number,
  totalScore: number,
  hideScores: boolean = false,
  includeCriteriaCheckmarks: boolean = false,
  isStudentMode: boolean = false,
  useGoogleDocsCompatibleLayout: boolean = false
) => {
  if (!checklist) return [];

  const numParagraphs = checklist.numParagraphs;

  const numParagraphColumns =
    useCalculateNumParagraphColumns(checklist.sections ?? []) ?? numParagraphs;

  const pageSize = useWordPageSize();
  const fontSize = useWordFontSize();
  const sections = [];

  const rows: TableRow[] = [];

  const cellMargins = useChecklistCellMargins();
  const overrideParagraphText: RevisionParagraph[] = [];

  const cellTextToParagraphs: CellTextParagraphs[] = [];

  for (const cell of wordChecklistCells) {
    const cellText = [];

    for (const paragraph of cell.paragraphs) {
      cellText.push(paragraph.text);

      overrideParagraphText.push(paragraph);
    }

    const cellTextString = cellText.join(" ");
    cellTextToParagraphs.push({
      text: cellTextString,
      paragraphs: cell.paragraphs,
    });
  }

  // Header row
  const headerRow = new TableRow({
    children: [
      new TableCell({
        margins: cellMargins,
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: "X",
                size: fontSize,
              }),
            ],
            alignment: AlignmentType.CENTER,
          }),
        ],
      }),
      new TableCell({
        margins: cellMargins,

        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: "Task",
                size: fontSize,
              }),
            ],
          }),
        ],
      }),
      new TableCell({
        margins: cellMargins,

        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: "Points",
                size: fontSize,
              }),
            ],
            alignment: AlignmentType.CENTER,
          }),
        ],
      }),
    ],
  });

  // rows.push(headerRow);

  for (const criteriaInstance of checklist.criteriaInstances) {
    var criteriaRows = useWordChecklistCriteriaGenerator(
      criteriaInstance,
      allCriteria,
      numParagraphs,
      numParagraphColumns,
      cellTextToParagraphs,
      AlignmentType.LEFT,
      "#000000",
      hideScores,
      includeCriteriaCheckmarks,
      isStudentMode,
      useGoogleDocsCompatibleLayout
    );
    rows.push(...criteriaRows);
  }

  if (checklist!.sections) {
    for (const section of checklist!.sections) {
      if (
        section.paragraphPositionsData == undefined &&
        checklist.paragraphPositionsData
      ) {
        section.paragraphPositionsData = JSON.parse(
          JSON.stringify(checklist.paragraphPositionsData)
        );
      }


      
      useWordChecklistSectionGenerator(
        section,
        rows,
        allCriteria,
        false,
        numParagraphs,
        numParagraphColumns,
        cellTextToParagraphs,
        false,
        hideScores,
        includeCriteriaCheckmarks,
        isStudentMode,
        useGoogleDocsCompatibleLayout
      );
    }
  }

  // If the submitted document is late then we want to add the late section to the checklsit before the footer.
  if (submittedDocument.isLate) {
    var criteriaRows = useWordChecklistCriteriaGenerator(
      {
        isCustom: true,
        labelOverride: "Late",
        earnedPoints: checklist.isLateDeduction,
      } as any,
      allCriteria,
      numParagraphs,
      numParagraphColumns,
      cellTextToParagraphs,
      AlignmentType.RIGHT,
      "f00000",
      hideScores,
      includeCriteriaCheckmarks,
      isStudentMode,
      useGoogleDocsCompatibleLayout
    );
    rows.push(...criteriaRows);
  }

  if (checklist.customCriteriaInstances != undefined) {
    const filteredCustomCriteriaInstances =
      checklist.customCriteriaInstances.filter(
        (customCriteriaInstance) => customCriteriaInstance.labelOverride != ""
      );

    for (const customCriteriaInstance of filteredCustomCriteriaInstances) {
      var criteriaRows = useWordChecklistCriteriaGenerator(
        customCriteriaInstance,
        allCriteria,
        numParagraphs,
        numParagraphColumns,
        cellTextToParagraphs,
        AlignmentType.RIGHT,
        "f00000",
        hideScores,
        includeCriteriaCheckmarks,
        isStudentMode,
        useGoogleDocsCompatibleLayout
      );
      rows.push(...criteriaRows);
    }
  }

  const showTotal =
    (submittedDocument.documentSubmissionType == undefined ||
      submittedDocument.documentSubmissionType ==
        DocumentSubmissionType.finalDraft) &&
    submittedDocument.allowFinalDraftGradedState != true;

  if (showTotal == true) {
    const footerRow = new TableRow({
      children: [
        new TableCell({
          margins: cellMargins,
          children: [new Paragraph("")],
        }),
        new TableCell({
          margins: cellMargins,
          children: [
            new Paragraph({
              alignment: AlignmentType.RIGHT,
              children: [
                new TextRun({
                  text: `TOTAL`,
                  bold: true,
                  size: fontSize,
                }),
              ],
            }),
          ],
        }),
        new TableCell({
          columnSpan: 1,
          margins: cellMargins,
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: hideScores
                ? []
                : [
                    new TextRun({
                      text: `${totalEarnedScore.toString()}/${totalScore}`,
                      bold: true,
                      size: fontSize,
                    }),
                  ],
            }),
          ],
        }),
      ],
    });

    rows.push(footerRow);
  }

  if (showTotal != true && submittedDocument.documentGradedState != undefined) {
    const footerRow = new TableRow({
      children: [
        new TableCell({
          margins: cellMargins,
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new TextRun({
                  text: DocumentGradedStateLabels[
                    submittedDocument.documentGradedState
                  ],
                  bold: true,
                  size: fontSize,
                  color:
                    submittedDocument.documentGradedState ==
                    DocumentGradedState.incomplete
                      ? "#B2985C"
                      : "#5A6E4F",
                }),
              ],
            }),
          ],
          columnSpan: 3,
        }),
      ],
    });

    rows.push(footerRow);
  }

  const unit = checklist.unit ? `Unit ${checklist.unit} ` : "";
  const suffix = checklist.isAdvancedChecklist ? " Advanced Aditions" : "";
  const label =
    checklist.levelName == "Practicum"
      ? "Practicum Checklist"
      : "Composition Checklist";

  const title = new Paragraph({
    children: [
      new TextRun({
        text: `${unit}${label}${suffix}`,
        bold: true,
        size: 36,
      }),
    ],
  });

  const subtitlePrefix =
    checklist.levelName == "Practicum" ? "" : `${checklist.lessonName}: `;

  const subtitle = new Paragraph({
    children: [
      new TextRun({
        text: `${subtitlePrefix}${checklist.subject ?? checklist.title}`,
        size: 32,
      }),
    ],
  });

  const googleDocsScoreColumnWidth = Math.min(
    2600,
    Math.max(1100, numParagraphColumns * 480)
  );
  const googleDocsCriteriaColumnWidth =
    9638 - 426 - googleDocsScoreColumnWidth;

  const table = new Table({
    rows,
    width: {
      size: 100,
      type: WidthType.PERCENTAGE,
    },
    columnWidths: useGoogleDocsCompatibleLayout
      ? [426, googleDocsCriteriaColumnWidth, googleDocsScoreColumnWidth]
      : [426, 6000, 3212],
  });

  const allSections: Paragraph | Table[] = [title, subtitle, new Paragraph("")];

  let studentName: string | undefined;

  if (checklist.levelName?.includes("OSS")) {
    studentName = submittedDocument.enteredName;
  } else {
    studentName =
      submittedDocument.enteredName ?? submittedDocument.studentName;
  }

  if (studentName != undefined) {
    const enteredNameParagraph = new Paragraph({
      children: [
        new TextRun({
          text: `Name: ${studentName}`,
          size: fontSize,
        }),
      ],
    });

    allSections.push(enteredNameParagraph, new Paragraph(""));
  }

  allSections.push(table);

  const checklistSection: ISectionOptions = {
    properties: {
      page: pageSize,
      type: SectionType.CONTINUOUS,
    },
    children: allSections,
  };

  sections.push(checklistSection);

  return sections;
};
